import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, FileText, Package, Sparkles } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { apiPost } from "../api/portal-client";
import { classifyPortalError } from "../hooks/usePortalResource";
import { usePortalAuth, PortalApiError } from "../hooks/usePortalAuth";

export function LandingPage() {
  const navigate = useNavigate();
  const { login } = usePortalAuth();
  const [searchParams] = useSearchParams();
  // Premier accès (D13) : le lien de l'email de bienvenue porte ?setup=<jeton>.
  // Tant qu'il est présent, on affiche le formulaire de création de mot de passe
  // à la place du formulaire de connexion.
  const setupToken = searchParams.get("setup");
  const [tenant, setTenant] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/tenants?name=${encodeURIComponent(tenant)}`,
      );
      if (!res.ok) {
        setError("Entreprise introuvable. Vérifiez le nom.");
        setLoading(false);
        return;
      }
      const tenantData = (await res.json()) as { id: string };
      const result = await login(username.trim(), password, tenantData.id);
      navigate(result.mustChangePassword ? "/profile?changePassword=1" : "/dashboard");
    } catch (err) {
      // W7-B — un réseau coupé ne se lit plus « erreur de connexion », formule
      // qui désigne les identifiants et envoie le client les changer pour rien.
      // Hors-ligne d'abord, puis le message EXACT de l'API (« mot de passe
      // incorrect », « accès désactivé par votre fournisseur »), qu'on ne
      // réécrit pas : lui seul sait pourquoi il refuse.
      setError(
        classifyPortalError(err) === "offline"
          ? "Vous semblez hors ligne. Vérifiez votre connexion, puis réessayez — rien n'est perdu."
          : err instanceof PortalApiError
            ? err.message
            : "La connexion n'a pas abouti. Réessayez dans un instant.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <header className="max-w-5xl mx-auto flex items-center justify-between px-4 py-5">
        <div className="flex items-center gap-2">
          <span className="font-display text-2xl font-bold text-terracotta">Antò</span>
          <span className="hidden sm:inline text-sm text-muted">Le copilote des artisans alimentaires</span>
        </div>
        <a
          href="https://heyanto.fr"
          className="inline-flex min-h-tap items-center text-sm text-muted hover:text-terracotta"
        >
          Vous êtes artisan ? →
        </a>
      </header>

      <main className="max-w-5xl mx-auto px-4 pb-12">
        <section className="py-10 text-center">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-ink leading-tight max-w-3xl mx-auto">
            Votre espace client artisan
          </h1>
          <p className="mt-4 text-base text-muted max-w-2xl mx-auto">
            Téléchargez vos factures, bons de livraison, avoirs et tarifs. À tout moment.
          </p>
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <FeatureMini icon={ShieldCheck} title="HACCP" desc="Traçabilité et conformité automatiques." />
          <FeatureMini icon={FileText} title="Factures & BL" desc="Émission et signature zéro paperasse." />
          <FeatureMini icon={Package} title="Stocks & lots" desc="Du fournisseur au client final, chaque gramme tracé." />
          <FeatureMini icon={Sparkles} title="Copilote IA" desc="Un assistant qui connaît votre métier." />
        </section>

        {setupToken ? (
          <SetupPasswordCard token={setupToken} onDone={() => navigate("/", { replace: true })} />
        ) : (
        <section className="max-w-md mx-auto bg-card border border-border rounded-xl2 shadow-sm p-6">
          <h2 className="font-display text-2xl font-bold text-ink mb-4">Connexion</h2>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <Input
              label="Nom de votre fournisseur"
              name="tenant"
              placeholder="Ex. Charcuterie Bartoli"
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
              required
              autoComplete="organization"
            />
            <Input
              label="Identifiant"
              name="username"
              placeholder="Ex. BOUCHERIE_DUPONT"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
            />
            <div className="relative">
              <Input
                label="Mot de passe"
                name="password"
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                aria-label={showPwd ? "Masquer" : "Afficher"}
                onClick={() => setShowPwd((v) => !v)}
                // Cible tactile 44×44 (l'icône reste à 18 px, centrée). `bottom-0`
                // plutôt qu'un `top-[34px]` en dur : le bouton reste aligné sur le
                // champ même si le libellé passe sur deux lignes.
                className="absolute bottom-0 right-1 inline-flex h-11 w-11 items-center justify-center rounded-xl2 text-muted hover:text-ink"
              >
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            {loading && (
              <p role="status" className="text-sm text-muted">
                Connexion en cours…
              </p>
            )}
            <Button type="submit" disabled={loading}>
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
            <p className="text-xs text-muted text-center">
              Mot de passe oublié ? Contactez votre fournisseur pour réinitialiser vos accès.
            </p>
          </form>
        </section>
        )}

        <footer className="mt-16 pt-6 border-t border-border text-center">
          <p className="text-sm text-muted">
            Propulsé par{" "}
            <a href="https://heyanto.fr" className="text-terracotta font-semibold">
              Antò
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}

/**
 * Premier accès : le client choisit son mot de passe à partir du jeton reçu par
 * email. Le jeton porte lui-même l'identité (client + fournisseur) — rien d'autre
 * n'est demandé. Il est à usage unique : une deuxième soumission est refusée par
 * l'API, et le message le dit explicitement.
 */
function SetupPasswordCard({ token, onDone }: { token: string; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await apiPost("/auth/change-password", { setupToken: token, newPassword: password });
      setDone(true);
    } catch (err) {
      setError(err instanceof PortalApiError ? err.message : "Ce lien n'est plus valide.");
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <section className="max-w-md mx-auto bg-card border border-border rounded-xl2 shadow-sm p-6 text-center">
        <h2 className="font-display text-2xl font-bold text-ink mb-2">Mot de passe enregistré</h2>
        <p className="text-sm text-muted mb-5">
          Connectez-vous avec l'identifiant reçu par email et le mot de passe que vous venez de choisir.
        </p>
        <Button onClick={onDone}>Aller à la connexion</Button>
      </section>
    );
  }

  return (
    <section className="max-w-md mx-auto bg-card border border-border rounded-xl2 shadow-sm p-6">
      <h2 className="font-display text-2xl font-bold text-ink mb-1">Choisissez votre mot de passe</h2>
      <p className="text-sm text-muted mb-4">
        Premier accès à votre espace client. Ce lien est valable une seule fois.
      </p>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div className="relative">
          <Input
            label="Nouveau mot de passe (8 caractères minimum)"
            name="newPassword"
            type={show ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            aria-label={show ? "Masquer" : "Afficher"}
            onClick={() => setShow((v) => !v)}
            className="absolute bottom-0 right-1 inline-flex h-11 w-11 items-center justify-center rounded-xl2 text-muted hover:text-ink"
          >
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <Input
          label="Confirmer le mot de passe"
          name="confirmPassword"
          type={show ? "text" : "password"}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          autoComplete="new-password"
        />
        {error && <p className="text-sm text-danger">{error}</p>}
        <Button type="submit" disabled={loading}>
          {loading ? "Enregistrement..." : "Enregistrer mon mot de passe"}
        </Button>
        <p className="text-xs text-muted text-center">
          Lien expiré ou déjà utilisé ? Demandez un nouvel accès à votre fournisseur.
        </p>
      </form>
    </section>
  );
}

function FeatureMini({ icon: Icon, title, desc }: { icon: typeof ShieldCheck; title: string; desc: string }) {
  return (
    <div className="bg-card border border-border rounded-xl2 p-4 flex flex-col items-start gap-2">
      <div className="p-2 rounded-lg bg-cream text-terracotta">
        <Icon size={20} />
      </div>
      <h3 className="font-semibold text-sm text-ink">{title}</h3>
      <p className="text-xs text-muted leading-relaxed">{desc}</p>
    </div>
  );
}
